import { useState } from "react";
import { Table, Button, Modal, Form, Input, Select, Switch, Tabs, Tag, Popconfirm, message, Drawer, Divider, Space } from "antd";
import { ArrowLeft, Plus, Trash2, Pencil, ChevronRight } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import client from "../../../api-hooks/client";
import API_ENDPOINTS from "../../../constants/api-endpoints";
import {
  useJobTypeList, useJobTypeCreate, useJobTypeUpdate, useJobTypeDelete,
  usePipelineList, usePipelineCreate,
  useChecklistList, useChecklistCreate, useChecklistDelete,
  useFormList, useFormCreate, useFormDelete,
  useChecklistDetail, useFormDetail,
} from "../../../api-hooks/jobConfig";

export default function JobConfigPage() {
  const { companyId: cParam } = useParams<{ companyId: string }>();
  const cid = Number(cParam);
  const navigate = useNavigate();
  const [msgApi, ctx] = message.useMessage();

  // ── Job Types ──────────────────────────────────────────────────────────────
  const { data: jobTypes = [] } = useJobTypeList(cid);
  const [jtModal, setJtModal] = useState<{ open: boolean; record: any | null }>({ open: false, record: null });
  const [jtForm] = Form.useForm();
  const createJt = useJobTypeCreate(cid, { onSuccess: () => { msgApi.success("Job type saved."); setJtModal({ open: false, record: null }); jtForm.resetFields(); }, onError: (e) => msgApi.error(e?.message || "Error") });
  const updateJt = useJobTypeUpdate(cid, { onSuccess: () => { msgApi.success("Updated."); setJtModal({ open: false, record: null }); jtForm.resetFields(); }, onError: (e) => msgApi.error(e?.message || "Error") });
  const deleteJt = useJobTypeDelete(cid, { onSuccess: () => msgApi.success("Deleted.") });

  // ── Pipelines ──────────────────────────────────────────────────────────────
  const { data: pipelines = [] } = usePipelineList(cid);
  const [pipModal, setPipModal] = useState(false);
  const [pipForm] = Form.useForm();
  const createPip = usePipelineCreate(cid, { onSuccess: () => { msgApi.success("Pipeline created."); setPipModal(false); pipForm.resetFields(); }, onError: (e) => msgApi.error(e?.message || "Error") });

  // ── Checklists ─────────────────────────────────────────────────────────────
  const { data: checklists = [] } = useChecklistList(cid);
  const [clModal, setClModal] = useState(false);
  const [clForm] = Form.useForm();
  const createCl = useChecklistCreate(cid, { onSuccess: () => { msgApi.success("Checklist created."); setClModal(false); clForm.resetFields(); }, onError: (e) => msgApi.error(e?.message || "Error") });
  const deleteCl = useChecklistDelete(cid, { onSuccess: () => msgApi.success("Deleted.") });

  // ── Forms ──────────────────────────────────────────────────────────────────
  const { data: forms = [] } = useFormList(cid);
  const [fModal, setFModal] = useState(false);
  const [fForm] = Form.useForm();
  const createF = useFormCreate(cid, { onSuccess: () => { msgApi.success("Form created."); setFModal(false); fForm.resetFields(); }, onError: (e) => msgApi.error(e?.message || "Error") });
  const deleteF = useFormDelete(cid, { onSuccess: () => msgApi.success("Deleted.") });

  // ── Form Detail Drawer ─────────────────────────────────────────────────────
  const [formDetailId, setFormDetailId] = useState<number | null>(null);
  const { data: formDetail, refetch: refetchFormDetail } = useFormDetail(cid, formDetailId);
  const [sectionForm] = Form.useForm();
  const [fieldForm] = Form.useForm();
  const [activeSectionId, setActiveSectionId] = useState<number | null>(null);
  const qc = useQueryClient();

  async function addSection(vals: any) {
    try {
      await client.post(API_ENDPOINTS.CJ_SECTION_CREATE(cid, formDetailId!), vals);
      sectionForm.resetFields();
      refetchFormDetail();
      qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CJ_FORMS(cid)] });
      msgApi.success("Section added.");
    } catch { msgApi.error("Failed."); }
  }

  async function deleteSection(id: number) {
    try {
      await client.post(API_ENDPOINTS.CJ_SECTION_DELETE(cid, formDetailId!), { id });
      refetchFormDetail();
      msgApi.success("Section deleted.");
    } catch { msgApi.error("Failed."); }
  }

  async function addField(sectionId: number, vals: any) {
    try {
      await client.post(API_ENDPOINTS.CJ_FIELD_CREATE(cid, formDetailId!, sectionId), vals);
      fieldForm.resetFields();
      setActiveSectionId(null);
      refetchFormDetail();
      msgApi.success("Field added.");
    } catch { msgApi.error("Failed."); }
  }

  async function deleteField(sectionId: number, fieldId: number) {
    try {
      await client.post(API_ENDPOINTS.CJ_FIELD_DELETE(cid, formDetailId!, sectionId), { id: fieldId });
      refetchFormDetail();
      msgApi.success("Field deleted.");
    } catch { msgApi.error("Failed."); }
  }

  // ── Checklist Detail Drawer ────────────────────────────────────────────────
  const [clDetailId, setClDetailId] = useState<number | null>(null);
  const { data: clDetail, refetch: refetchClDetail } = useChecklistDetail(cid, clDetailId);
  const [itemForm] = Form.useForm();

  async function addItem(vals: any) {
    try {
      await client.post(API_ENDPOINTS.CJ_ITEM_CREATE(cid, clDetailId!), vals);
      itemForm.resetFields();
      refetchClDetail();
      msgApi.success("Item added.");
    } catch { msgApi.error("Failed."); }
  }

  async function deleteItem(id: number) {
    try {
      await client.post(API_ENDPOINTS.CJ_ITEM_DELETE(cid, clDetailId!), { id });
      refetchClDetail();
      msgApi.success("Item deleted.");
    } catch { msgApi.error("Failed."); }
  }

  const jtCols = [
    { title: "Name", dataIndex: "name", key: "name", render: (v: string, r: any) => <span className="font-medium" style={{ color: r.colour ?? undefined }}>{v}</span> },
    { title: "Slug", dataIndex: "slug", key: "slug", render: (v: string) => <span className="font-mono text-xs">{v}</span> },
    { title: "Duration", dataIndex: "default_duration_mins", key: "default_duration_mins", render: (v: number) => `${v} min` },
    { title: "Parts", dataIndex: "requires_parts", key: "requires_parts", render: (v: number) => <Tag color={v ? "green" : "default"}>{v ? "Yes" : "No"}</Tag> },
    { title: "Cert", dataIndex: "requires_cert", key: "requires_cert", render: (v: number) => <Tag color={v ? "blue" : "default"}>{v ? "Yes" : "No"}</Tag> },
    { title: "Photo", dataIndex: "requires_photo", key: "requires_photo", render: (v: number) => <Tag color={v ? "purple" : "default"}>{v ? "Yes" : "No"}</Tag> },
    { title: "Sig", dataIndex: "requires_signature", key: "requires_signature", render: (v: number) => <Tag color={v ? "orange" : "default"}>{v ? "Yes" : "No"}</Tag> },
    { title: "Active", dataIndex: "is_active", key: "is_active", render: (v: number) => <Tag color={v ? "green" : "red"}>{v ? "Yes" : "No"}</Tag> },
    {
      title: "", key: "actions", dataIndex: "id", render: (_: any, r: any) => (
        <div className="flex gap-2">
          <Button size="small" icon={<Pencil size={13} />} onClick={() => { setJtModal({ open: true, record: r }); jtForm.setFieldsValue(r); }} />
          <Popconfirm title="Delete job type?" onConfirm={() => deleteJt.mutate(r.id)}><Button size="small" danger icon={<Trash2 size={13} />} /></Popconfirm>
        </div>
      ),
    },
  ];

  const pipelineCols = [
    { title: "Pipeline", dataIndex: "name", key: "name", render: (v: string) => <span className="font-medium">{v}</span> },
    { title: "Job Type", dataIndex: "job_type_id", key: "job_type_id", render: (v: number) => { const jt = jobTypes.find((j: any) => j.id === v); return jt ? <Tag>{(jt as any).name}</Tag> : "—"; } },
    { title: "Default", dataIndex: "is_default", key: "is_default", render: (v: number) => v ? <Tag color="blue">Default</Tag> : "—" },
    {
      title: "Stages", key: "stages", dataIndex: "id", render: (_: any, r: any) => (
        <div className="flex flex-wrap gap-1">
          {(r.stages || []).map((s: any) => (
            <Tag key={s.id} color={s.is_complete ? "green" : s.is_cancelled ? "red" : "default"}>{s.name}</Tag>
          ))}
        </div>
      ),
    },
  ];

  const clCols = [
    { title: "Name", dataIndex: "name", key: "name", render: (v: string) => <span className="font-medium">{v}</span> },
    { title: "Description", dataIndex: "description", key: "description", render: (v: string) => v || "—" },
    { title: "Active", dataIndex: "is_active", key: "is_active", render: (v: number) => <Tag color={v ? "green" : "red"}>{v ? "Active" : "Inactive"}</Tag> },
    {
      title: "", key: "actions", render: (_: any, r: any) => (
        <Space size={4}>
          <Button size="small" icon={<ChevronRight size={13} />} onClick={() => setClDetailId(r.id)}>Items</Button>
          <Popconfirm title="Delete checklist?" onConfirm={() => deleteCl.mutate(r.id)}><Button size="small" danger icon={<Trash2 size={13} />} /></Popconfirm>
        </Space>
      ),
    },
  ];

  const formCols = [
    { title: "Name", dataIndex: "name", key: "name", render: (v: string) => <span className="font-medium">{v}</span> },
    { title: "Description", dataIndex: "description", key: "description", render: (v: string) => v || "—" },
    { title: "Active", dataIndex: "is_active", key: "is_active", render: (v: number) => <Tag color={v ? "green" : "red"}>{v ? "Active" : "Inactive"}</Tag> },
    {
      title: "", key: "actions", render: (_: any, r: any) => (
        <Space size={4}>
          <Button size="small" icon={<ChevronRight size={13} />} onClick={() => setFormDetailId(r.id)}>Sections</Button>
          <Popconfirm title="Delete form template?" onConfirm={() => deleteF.mutate(r.id)}><Button size="small" danger icon={<Trash2 size={13} />} /></Popconfirm>
        </Space>
      ),
    },
  ];

  const tabItems = [
    {
      key: "job-types", label: `Job Types (${jobTypes.length})`,
      children: (
        <div>
          <div className="flex justify-between mb-3">
            <span className="text-sm text-slate-500">Define types of work this company performs</span>
            <Button type="primary" icon={<Plus size={14} />} onClick={() => setJtModal({ open: true, record: null })}>Add Job Type</Button>
          </div>
          <Table dataSource={jobTypes} columns={jtCols} rowKey="id" pagination={false} scroll={{ x: "max-content" }} size="small" />
        </div>
      ),
    },
    {
      key: "pipelines", label: `Pipelines (${pipelines.length})`,
      children: (
        <div>
          <div className="flex justify-between mb-3">
            <span className="text-sm text-slate-500">Job status workflows with stages</span>
            <Button type="primary" icon={<Plus size={14} />} onClick={() => setPipModal(true)}>Add Pipeline</Button>
          </div>
          <Table dataSource={pipelines} columns={pipelineCols} rowKey="id" pagination={false} size="small" />
        </div>
      ),
    },
    {
      key: "checklists", label: `Checklists (${checklists.length})`,
      children: (
        <div>
          <div className="flex justify-between mb-3">
            <span className="text-sm text-slate-500">Pre/post-job safety and quality checklists</span>
            <Button type="primary" icon={<Plus size={14} />} onClick={() => setClModal(true)}>Add Checklist</Button>
          </div>
          <Table dataSource={checklists} columns={clCols} rowKey="id" pagination={false} size="small" />
        </div>
      ),
    },
    {
      key: "forms", label: `Form Templates (${forms.length})`,
      children: (
        <div>
          <div className="flex justify-between mb-3">
            <span className="text-sm text-slate-500">Dynamic forms attached to jobs</span>
            <Button type="primary" icon={<Plus size={14} />} onClick={() => setFModal(true)}>Add Form</Button>
          </div>
          <Table dataSource={forms} columns={formCols} rowKey="id" pagination={false} size="small" />
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      {ctx}
      <div className="flex items-center gap-3 mb-6">
        <Button icon={<ArrowLeft size={16} />} onClick={() => navigate(-1)} />
        <div>
          <h1 className="text-xl font-semibold text-slate-800 mb-0">Job Configuration</h1>
          <p className="text-slate-500 text-sm">Company ID: {cid}</p>
        </div>
      </div>

      <Tabs items={tabItems} />

      {/* Job Type Modal */}
      <Modal title={jtModal.record ? "Edit Job Type" : "Add Job Type"} open={jtModal.open} onCancel={() => { setJtModal({ open: false, record: null }); jtForm.resetFields(); }} footer={null} width={560}>
        <Form form={jtForm} layout="vertical" onFinish={(vals) => {
          if (jtModal.record) updateJt.mutate({ ...vals, id: jtModal.record.id });
          else createJt.mutate(vals);
        }} className="mt-4">
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="slug" label="Slug (auto if blank)"><Input /></Form.Item>
            <Form.Item name="colour" label="Colour"><Input placeholder="#2563EB" /></Form.Item>
            <Form.Item name="default_duration_mins" label="Default Duration (mins)" initialValue={60}><Input type="number" /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="requires_parts" valuePropName="checked" label="Requires Parts"><Switch /></Form.Item>
            <Form.Item name="requires_cert" valuePropName="checked" label="Requires Cert"><Switch /></Form.Item>
            <Form.Item name="requires_photo" valuePropName="checked" label="Requires Photo"><Switch /></Form.Item>
            <Form.Item name="requires_signature" valuePropName="checked" label="Requires Signature"><Switch /></Form.Item>
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={() => { setJtModal({ open: false, record: null }); jtForm.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={createJt.isPending || updateJt.isPending}>Save</Button>
          </div>
        </Form>
      </Modal>

      {/* Pipeline Modal */}
      <Modal title="Add Pipeline" open={pipModal} onCancel={() => { setPipModal(false); pipForm.resetFields(); }} footer={null}>
        <Form form={pipForm} layout="vertical" onFinish={(vals) => createPip.mutate(vals)} className="mt-4">
          <Form.Item name="name" label="Pipeline Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="job_type_id" label="Job Type (optional)">
            <Select allowClear options={jobTypes.map((j: any) => ({ label: j.name, value: j.id }))} />
          </Form.Item>
          <Form.Item name="is_default" valuePropName="checked" label="Default Pipeline"><Switch /></Form.Item>
          <div className="flex justify-end gap-2">
            <Button onClick={() => { setPipModal(false); pipForm.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={createPip.isPending}>Create</Button>
          </div>
        </Form>
      </Modal>

      {/* Checklist Modal */}
      <Modal title="Add Checklist" open={clModal} onCancel={() => { setClModal(false); clForm.resetFields(); }} footer={null}>
        <Form form={clForm} layout="vertical" onFinish={(vals) => createCl.mutate(vals)} className="mt-4">
          <Form.Item name="name" label="Checklist Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="job_type_id" label="Job Type (optional)">
            <Select allowClear options={jobTypes.map((j: any) => ({ label: j.name, value: j.id }))} />
          </Form.Item>
          <div className="flex justify-end gap-2">
            <Button onClick={() => { setClModal(false); clForm.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={createCl.isPending}>Create</Button>
          </div>
        </Form>
      </Modal>

      {/* Form Template Modal */}
      <Modal title="Add Form Template" open={fModal} onCancel={() => { setFModal(false); fForm.resetFields(); }} footer={null}>
        <Form form={fForm} layout="vertical" onFinish={(vals) => createF.mutate(vals)} className="mt-4">
          <Form.Item name="name" label="Form Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="job_type_id" label="Job Type (optional)">
            <Select allowClear options={jobTypes.map((j: any) => ({ label: j.name, value: j.id }))} />
          </Form.Item>
          <div className="flex justify-end gap-2">
            <Button onClick={() => { setFModal(false); fForm.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={createF.isPending}>Create</Button>
          </div>
        </Form>
      </Modal>

      {/* Form Template Detail Drawer */}
      <Drawer
        title={formDetail ? `Form: ${formDetail.name}` : "Form Sections"}
        open={!!formDetailId}
        onClose={() => { setFormDetailId(null); setActiveSectionId(null); }}
        width={580}
      >
        {formDetail && (
          <div className="space-y-4">
            {/* Add Section */}
            <div>
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">Add Section</div>
              <Form form={sectionForm} layout="inline" onFinish={addSection}>
                <Form.Item name="title" rules={[{ required: true }]} className="flex-1">
                  <Input placeholder="Section title" />
                </Form.Item>
                <Button type="primary" htmlType="submit" size="small" icon={<Plus size={13} />}>Add</Button>
              </Form>
            </div>
            <Divider className="my-2" />

            {/* Sections list */}
            {(formDetail.sections || []).length === 0 && (
              <div className="text-slate-400 text-sm text-center py-6">No sections yet. Add one above.</div>
            )}
            {(formDetail.sections || []).map((sec: any) => (
              <div key={sec.id} className="border border-slate-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-slate-700">{sec.title}</span>
                  <Space size={4}>
                    <Button size="small" icon={<Plus size={12} />} onClick={() => setActiveSectionId(activeSectionId === sec.id ? null : sec.id)}>
                      Field
                    </Button>
                    <Popconfirm title="Delete section and all its fields?" onConfirm={() => deleteSection(sec.id)}>
                      <Button size="small" danger icon={<Trash2 size={12} />} />
                    </Popconfirm>
                  </Space>
                </div>

                {/* Fields */}
                {(sec.fields || []).map((f: any) => (
                  <div key={f.id} className="flex items-center justify-between py-1 px-2 bg-slate-50 rounded mb-1">
                    <div>
                      <span className="text-sm text-slate-700">{f.field_label}</span>
                      <Tag className="ml-2" color="default">{f.field_type}</Tag>
                      {f.is_required ? <Tag color="orange">Required</Tag> : null}
                    </div>
                    <Popconfirm title="Delete field?" onConfirm={() => deleteField(sec.id, f.id)}>
                      <Button size="small" danger type="text" icon={<Trash2 size={11} />} />
                    </Popconfirm>
                  </div>
                ))}

                {/* Add Field inline */}
                {activeSectionId === sec.id && (
                  <Form form={fieldForm} layout="inline" onFinish={(vals) => addField(sec.id, vals)} className="mt-2">
                    <Form.Item name="field_label" rules={[{ required: true }]}>
                      <Input placeholder="Field label" size="small" style={{ width: 160 }} />
                    </Form.Item>
                    <Form.Item name="field_type" initialValue="text">
                      <Select size="small" style={{ width: 110 }} options={[
                        { value: "text", label: "Text" },
                        { value: "textarea", label: "Textarea" },
                        { value: "number", label: "Number" },
                        { value: "date", label: "Date" },
                        { value: "select", label: "Select" },
                        { value: "checkbox", label: "Checkbox" },
                        { value: "photo", label: "Photo" },
                        { value: "signature", label: "Signature" },
                      ]} />
                    </Form.Item>
                    <Form.Item name="is_required" valuePropName="checked">
                      <Switch size="small" checkedChildren="Req" unCheckedChildren="Opt" />
                    </Form.Item>
                    <Button size="small" type="primary" htmlType="submit" icon={<Plus size={11} />}>Add</Button>
                    <Button size="small" onClick={() => setActiveSectionId(null)}>Cancel</Button>
                  </Form>
                )}
              </div>
            ))}
          </div>
        )}
      </Drawer>

      {/* Checklist Detail Drawer */}
      <Drawer
        title={clDetail ? `Checklist: ${clDetail.name}` : "Checklist Items"}
        open={!!clDetailId}
        onClose={() => setClDetailId(null)}
        width={500}
      >
        {clDetail && (
          <div className="space-y-4">
            {/* Add Item */}
            <div>
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">Add Item</div>
              <Form form={itemForm} layout="vertical" onFinish={addItem}>
                <Form.Item name="item_text" rules={[{ required: true }]}>
                  <Input.TextArea rows={2} placeholder="Item text / instruction" />
                </Form.Item>
                <div className="flex gap-3">
                  <Form.Item name="response_type" label="Response" initialValue="checkbox" className="flex-1">
                    <Select options={[
                      { value: "checkbox", label: "Checkbox" },
                      { value: "pass_fail", label: "Pass/Fail" },
                      { value: "text", label: "Text" },
                      { value: "number", label: "Number" },
                      { value: "photo", label: "Photo" },
                      { value: "signature", label: "Signature" },
                    ]} />
                  </Form.Item>
                  <Form.Item name="is_required" label="Required" valuePropName="checked" initialValue={true}>
                    <Switch />
                  </Form.Item>
                  <Form.Item name="fail_triggers_alert" label="Alert on Fail" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </div>
                <Button type="primary" htmlType="submit" icon={<Plus size={13} />} block>Add Item</Button>
              </Form>
            </div>
            <Divider className="my-2" />

            {/* Items list */}
            {(clDetail.items || []).length === 0 && (
              <div className="text-slate-400 text-sm text-center py-6">No items yet.</div>
            )}
            {(clDetail.items || []).map((item: any, idx: number) => (
              <div key={item.id} className="flex items-start justify-between py-2 px-3 bg-slate-50 rounded border border-slate-100">
                <div>
                  <div className="text-sm font-medium text-slate-700">{idx + 1}. {item.item_text}</div>
                  <div className="flex gap-1 mt-1">
                    <Tag color="default">{item.response_type}</Tag>
                    {item.is_required ? <Tag color="orange">Required</Tag> : null}
                    {item.fail_triggers_alert ? <Tag color="red">Alert</Tag> : null}
                  </div>
                </div>
                <Popconfirm title="Delete item?" onConfirm={() => deleteItem(item.id)}>
                  <Button size="small" danger type="text" icon={<Trash2 size={12} />} />
                </Popconfirm>
              </div>
            ))}
          </div>
        )}
      </Drawer>
    </div>
  );
}
